import {Cloudinary, CloudinaryImage} from "@cloudinary/url-gen"
import {crop, fill, pad, limitPad, fit} from "@cloudinary/url-gen/actions/resize"
import {grayscale} from "@cloudinary/url-gen/actions/effect"
import {sharpen} from "@cloudinary/url-gen/actions/adjust"
import {dpr} from "@cloudinary/url-gen/actions/delivery"
import {max as maxRoundCorners} from "@cloudinary/url-gen/actions/roundCorners"

import logoAlt from "@irati/ui/images/logo-alt.svg"
import {PROFILE_IMAGE_SIZE} from "@irati-web/platform/src/constants/profile"

type ResizeTransformation = typeof fill | typeof limitPad

const cloudinary = new Cloudinary({
  cloud: {
    cloudName: "perkbox"
  },
  url: {
    secure: true
  }
})

// Local methods //

const isMobile = () => {
  const mobile = 768
  return window.innerWidth && window.innerWidth < mobile
}

const defaultImage = (width: number, height: number) => {
  return `https://via.placeholder.com/${width}x${height}/BCBECE?text=+`
}

const defaultResize = (width: number | undefined, height: number | undefined) => {
  return crop(width, height)
}

const getCloudinaryId = (url?: string) => {
  return url?.split("/perkbox/image/upload/")[1]
}

const isCloudinaryUrl = (url?: string) => {
  return url && getCloudinaryId(url)
}

function withDpr(image: CloudinaryImage) {
  const isDoubleDensityDevice = window.devicePixelRatio && window.devicePixelRatio >= 2
  if (isDoubleDensityDevice) {
    image.delivery(dpr("2.0"))
  }
  return image.toURL()
}

const rgbToHex = (rgb: string[]) => {
  const rgbNum = rgb
    .map((x) => {
      const hex = x.toString(16)
      return hex.length === 1 ? `0${hex}` : hex
    })
    .join("")
  return `#${rgbNum}`
}

// Exports //

export const getBannerImage = (url?: string) => {
  const width = isMobile() ? 1600 : 1920
  const height = isMobile() ? 600 : 350
  if (!isCloudinaryUrl(url)) {
    return url || defaultImage(width, height)
  }

  const image = cloudinary.image(getCloudinaryId(url))
  image.resize(fill(width, height))
  image.resize(defaultResize(width, height))

  return image.toURL()
}

export const resizeCloudinaryImage = (url: string, width: number, height: number) => {
  if (!isCloudinaryUrl(url)) {
    return url || defaultImage(width, height)
  }

  const image = cloudinary.image(getCloudinaryId(url))
  image.resize(fill(width, height))
  image.resize(defaultResize(width, height))

  return image.toURL()
}

export const getDealTileImage = (url?: string, transformationType: ResizeTransformation = fill) => {
  const width = isMobile() ? 600 : 720
  const height = isMobile() ? 206 : 320
  if (!isCloudinaryUrl(url)) {
    return url || defaultImage(width, height)
  }

  const image = cloudinary.image(getCloudinaryId(url))
  image.resize(transformationType().width(width).height(height))
  image.resize(defaultResize(width, height))

  return image.toURL()
}

export const getDealTileImageWithPadding = (url: string) => getDealTileImage(url, limitPad)

export const getThumbnailImage = (url?: string, opts = {grayscale: false}) => {
  const size = isMobile() ? 320 : 640
  if (!isCloudinaryUrl(url)) {
    return url || defaultImage(size, size)
  }

  const image = cloudinary.image(getCloudinaryId(url))
  image.resize(fit(size, size))

  if (opts?.grayscale) {
    image.effect(grayscale())
  }

  return image.toURL()
}

export const getProfileImage = (url: string, defaultImageOverride?: string) => {
  const size = PROFILE_IMAGE_SIZE
  if (!isCloudinaryUrl(url)) {
    return defaultImageOverride || defaultImage(size, size)
  }

  const image = cloudinary.image(getCloudinaryId(url))
  image.roundCorners(maxRoundCorners())
  image.resize(fill(size, size))

  return withDpr(image)
}

export const getBrandLogo = (url: string, width: number, height: number, sharpnessLevel = 100, shouldPad = false) => {
  if (!isCloudinaryUrl(url)) {
    return logoAlt
  }

  const image = cloudinary.image(getCloudinaryId(url))
  if (shouldPad) {
    image.resize(pad(width, height))
  } else {
    image.resize(defaultResize(width, height))
  }
  image.adjust(sharpen().strength(sharpnessLevel))

  return withDpr(image)
}

export const getColorFromThumbnail = (image: string): Promise<string> => {
  const colorThief = new window.ColorThief()
  return new Promise((resolve) => {
    const img = document.createElement("img")
    img.crossOrigin = "Anonymous"
    img.onload = function () {
      const rgb = colorThief.getColor(img)
      resolve(rgbToHex(rgb))
    }
    img.src = image
  })
}
